import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getRajasthanDemandMetrics,
  getDiscomDrawalScheduleMetrics,
  getTotalDrawalScheduleMetrics,
  getRajasthanDrawalScheduleForecastMetrics,
} from "../Utility/RestApi";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useDispatch, useSelector } from "react-redux";
import { getTimeRange } from "../../Dashboard/Exchange3/Chart";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { formatDateForAPI } from "../Utility/const";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip, { MetricChipHead } from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { TrendData } from "../../../models/metrics";
const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

const GROUP_BY = [
  "Discom Load",
  "Total Discom Load",
  "RVPN Drawal",
  "Total Load & RVPN Drawal",
];

const DISCOM = [
  { label: "JDVVNL", key: "JDVVNL" },
  { label: "JVVNL", key: "JVVNL" },
  { label: "AVVNL", key: "AVVNL" },
];

const TYPE = ["Schedule", "Drawal"];
const FORECAST_TYPES = ["Actual", "Forecast D+1", "Forecast D+2"];

interface RajasthanSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: string;
  onGroupByChange: (index: number) => void;
  selectedSuffix: any[];
  onTypeChange: (selected: string[]) => void;
  chartSuffixes: any[];
  metrics: TrendData | null;
}

export function RajasthanSummaryAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedSuffix,
  onTypeChange,
  chartSuffixes,
  metrics,
}: RajasthanSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const dispatch = useDispatch();
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [discomData, setDiscomData] = useState<any[]>([]);
  const [totalDiscomData, setTotalDiscomData] = useState<any[]>([]);
  const [forecastData, setForecastData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedForecastType, setSelectedForecastType] = useState<string[]>([
    "Actual",
  ]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const trendDates = DefaultDates[activeTab]?.defaultTrendDates || [];
  const isActualDates =
    trendDates.length > 0 &&
    trendDates.some((d: any) => !d.label?.includes("(Forecast)"));
  const isForecastDates =
    trendDates.length > 0 &&
    (trendDates.some((d: any) => d.label?.includes("(Forecast)")) ||
      selectedForecastType.some((t: string) => t.includes("Forecast")));

  const fetchSummaryData = useCallback(async () => {
    setLoading(true);

    try {
      const allDates = trendDates.flatMap((dateRange: any) => {
        const [start, end] = dateRange.value.split(",");
        return [start, end].filter(Boolean);
      });

      if (allDates.length === 0) {
        console.error("No valid dates found");
        setSummaryData([]);
        setDiscomData([]);
        setTotalDiscomData([]);
        setForecastData([]);
        setLoading(false);
        return;
      }

      const sortedDates = allDates.sort(
        (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
      );
      const startDateStr = sortedDates[0];
      const endDateStr = sortedDates[sortedDates.length - 1];

      const [
        rvpnActualRes,
        discomMetricsRes,
        totalDiscomMetricsRes,
        forecastMetricsRes,
      ] = await Promise.all([
        isActualDates
          ? getRajasthanDemandMetrics(startDateStr, endDateStr)
          : Promise.resolve([]),
        isActualDates
          ? getDiscomDrawalScheduleMetrics(startDateStr, endDateStr)
          : Promise.resolve([]),
        isActualDates
          ? getTotalDrawalScheduleMetrics(startDateStr, endDateStr)
          : Promise.resolve([]),
        isForecastDates ||
          selectedForecastType.some((type) => type.includes("Forecast"))
          ? getRajasthanDrawalScheduleForecastMetrics(startDateStr, endDateStr)
          : Promise.resolve([]),
      ]);

      setSummaryData(Array.isArray(rvpnActualRes) ? rvpnActualRes : []);
      setDiscomData(Array.isArray(discomMetricsRes) ? discomMetricsRes : []);
      setTotalDiscomData(
        Array.isArray(totalDiscomMetricsRes) ? totalDiscomMetricsRes : [],
      );
      setForecastData(
        Array.isArray(forecastMetricsRes) ? forecastMetricsRes : [],
      );
    } catch (error) {
      console.error("Error fetching summary data:", error);
      setSummaryData([]);
      setDiscomData([]);
      setTotalDiscomData([]);
      setForecastData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedForecastType]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  useEffect(() => {
    const conversionFactor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    if (selectedGroupKeys === "RVPN Drawal") {
      let dataToProcess: any[] = [];

      if (selectedForecastType.includes("Actual") && isActualDates) {
        dataToProcess = summaryData.map((item) => ({
          slot: item.slot,
          slot_number: item.slot,
          slot_with_time:
            item.slot && item.time_string
              ? `${item.slot} (${item.time_string})`
              : item.slot,
          min_drawal: item.min_demand * conversionFactor,
          max_drawal: item.max_demand * conversionFactor,
          avg_drawal: item.avg_demand * conversionFactor,
          mode_drawal_window_formula:
            item.mode_demand_window_formula * conversionFactor,
          date_of_min_drawal: item.date_of_min,
          date_of_max_drawal: item.date_of_max,
          forecast_type: "Actual",
        }));
      }

      if (selectedForecastType.includes("Forecast D+1") && isForecastDates) {
        const t1Data = forecastData
          .filter(
            (item) => item.forecast_type === "T+1" && item.discom === "Total",
          )
          .map((item) => ({
            slot: item.block,
            slot_number: item.block,
            slot_with_time:
              item.block && item.time_string
                ? `${item.block} (${item.time_string})`
                : item.block,
            min_drawal_t1: (item.min_drawal || 0) * conversionFactor,
            max_drawal_t1: (item.max_drawal || 0) * conversionFactor,
            avg_drawal_t1: (item.avg_drawal || 0) * conversionFactor,
            mode_drawal_window_formula_t1:
              (item.mode_drawal_window_formula || 0) * conversionFactor,
            date_of_min_drawal_t1: item.date_of_min_drawal,
            date_of_max_drawal_t1: item.date_of_max_drawal,
            forecast_type: "T+1",
          }));

        if (dataToProcess.length > 0) {
          dataToProcess = dataToProcess.map((actual) => {
            const t1 = t1Data.find((t) => t.slot === actual.slot);
            return t1 ? { ...actual, ...t1 } : actual;
          });
        } else {
          dataToProcess = t1Data;
        }
      }

      if (selectedForecastType.includes("Forecast D+2") && isForecastDates) {
        const t2Data = forecastData
          .filter(
            (item) => item.forecast_type === "T+2" && item.discom === "Total",
          )
          .map((item) => ({
            slot: item.block,
            slot_number: item.block,
            slot_with_time:
              item.block && item.time_string
                ? `${item.block} (${item.time_string})`
                : item.block,
            min_drawal_t2: (item.min_drawal || 0) * conversionFactor,
            max_drawal_t2: (item.max_drawal || 0) * conversionFactor,
            avg_drawal_t2: (item.avg_drawal || 0) * conversionFactor,
            mode_drawal_window_formula_t2:
              (item.mode_drawal_window_formula || 0) * conversionFactor,
            date_of_min_drawal_t2: item.date_of_min_drawal,
            date_of_max_drawal_t2: item.date_of_max_drawal,
            forecast_type: "T+2",
          }));

        if (dataToProcess.length > 0) {
          dataToProcess = dataToProcess.map((existing) => {
            const t2 = t2Data.find((t) => t.slot === existing.slot);
            return t2 ? { ...existing, ...t2 } : existing;
          });
        } else {
          dataToProcess = t2Data;
        }
      }

      dataToProcess.sort((a, b) => a.slot - b.slot);

      setChartData(dataToProcess);
      setTableData(dataToProcess);
      setChartKeys([]);

      let finalData = dataToProcess;
      if (selectedBlock.length > 0) {
        finalData = finalData.filter((item) =>
          selectedBlock.map((b) => b.value).includes(item.slot),
        );
      }
      setChartData(finalData);
      setTableData(finalData);
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      const slotMap = new Map<number, any>();

      totalDiscomData.forEach((item) => {
        const slot = item.slot;
        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot: slot,
            slot_number: slot,
            slot_with_time:
              item.slot && item.time_string
                ? `${item.slot} (${item.time_string})`
                : item.slot,
          });
        }

        const slotData = slotMap.get(slot);
        slotData[`total_discom_min_drawal`] =
          (item.min_drawal || 0) * conversionFactor;
        slotData[`total_discom_max_drawal`] =
          (item.max_drawal || 0) * conversionFactor;
        slotData[`total_discom_avg_drawal`] =
          (item.avg_drawal || 0) * conversionFactor;
        slotData[`total_discom_mode_drawal_window_formula`] =
          (item.mode_drawal_window_formula || 0) * conversionFactor;
        slotData[`total_discom_date_of_min_drawal`] = item.date_of_min_drawal;
        slotData[`total_discom_date_of_max_drawal`] = item.date_of_max_drawal;
      });

      if (selectedForecastType.includes("Actual") && isActualDates) {
        summaryData.forEach((item) => {
          const slot = item.slot;
          if (!slotMap.has(slot)) {
            slotMap.set(slot, {
              slot: slot,
              slot_number: slot,
              slot_with_time:
                item.slot && item.time_string
                  ? `${item.slot} (${item.time_string})`
                  : item.slot,
            });
          }

          const slotData = slotMap.get(slot);
          slotData[`rvpn_min_drawal`] = item.min_demand * conversionFactor;
          slotData[`rvpn_max_drawal`] = item.max_demand * conversionFactor;
          slotData[`rvpn_avg_drawal`] = item.avg_demand * conversionFactor;
          slotData[`rvpn_mode_drawal_window_formula`] =
            item.mode_demand_window_formula * conversionFactor;
          slotData[`rvpn_date_of_min_drawal`] = item.date_of_min;
          slotData[`rvpn_date_of_max_drawal`] = item.date_of_max;
        });
      }

      const processedData = Array.from(slotMap.values());
      processedData.sort((a, b) => a.slot - b.slot);

      let finalData = processedData;
      if (selectedBlock.length > 0) {
        finalData = finalData.filter((item) =>
          selectedBlock.map((b) => b.value).includes(item.slot),
        );
      }
      setChartData(finalData);
      setTableData(finalData);

      const keys = ["total_discom"];
      if (selectedForecastType.includes("Actual") && isActualDates) {
        keys.push("rvpn");
      }
      setChartKeys(keys);
    } else if (selectedGroupKeys === "Total Discom Load") {
      const slotMap = new Map<number, any>();

      totalDiscomData.forEach((item) => {
        const slot = item.slot;
        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot: slot,
            slot_number: slot,
            slot_with_time:
              item.slot && item.time_string
                ? `${item.slot} (${item.time_string})`
                : item.slot,
          });
        }

        const slotData = slotMap.get(slot);
        slotData.min_drawal = (item.min_drawal || 0) * conversionFactor;
        slotData.max_drawal = (item.max_drawal || 0) * conversionFactor;
        slotData.avg_drawal = (item.avg_drawal || 0) * conversionFactor;
        slotData.mode_drawal_window_formula =
          (item.mode_drawal_window_formula || 0) * conversionFactor;
        slotData.min_schedule = (item.min_schedule || 0) * conversionFactor;
        slotData.max_schedule = (item.max_schedule || 0) * conversionFactor;
        slotData.avg_schedule = (item.avg_schedule || 0) * conversionFactor;
        slotData.mode_schedule_window_formula =
          (item.mode_schedule_window_formula || 0) * conversionFactor;
        slotData.date_of_min_drawal = item.date_of_min_drawal;
        slotData.date_of_max_drawal = item.date_of_max_drawal;
        slotData.date_of_min_schedule = item.date_of_min_schedule;
        slotData.date_of_max_schedule = item.date_of_max_schedule;
      });

      const processedData = Array.from(slotMap.values());
      processedData.sort((a, b) => a.slot - b.slot);

      let finalData = processedData;
      if (selectedBlock.length > 0) {
        finalData = finalData.filter((item) =>
          selectedBlock.map((b) => b.value).includes(item.slot),
        );
      }
      setChartData(finalData);
      setTableData(finalData);
      setChartKeys([]);
    } else {
      let filteredDiscomData = discomData;

      if (selectedDiscom.length > 0 && selectedDiscom.length < DISCOM.length) {
        filteredDiscomData = filteredDiscomData.filter((item) =>
          selectedDiscom.includes(item.discom),
        );
      }

      const slotMap = new Map<number, any>();

      filteredDiscomData.forEach((item) => {
        const slot = item.slot;
        const discom = item.discom;

        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot: slot,
            slot_number: slot,
            slot_with_time:
              item.slot && item.time_string
                ? `${item.slot} (${item.time_string})`
                : item.slot,
          });
        }

        const slotData = slotMap.get(slot);

        slotData[`${discom}_min_drawal`] = item.min_drawal * conversionFactor;
        slotData[`${discom}_max_drawal`] = item.max_drawal * conversionFactor;
        slotData[`${discom}_avg_drawal`] = item.avg_drawal * conversionFactor;
        slotData[`${discom}_mode_drawal_window_formula`] =
          item.mode_drawal_window_formula * conversionFactor;

        slotData[`${discom}_min_schedule`] =
          item.min_schedule * conversionFactor;
        slotData[`${discom}_max_schedule`] =
          item.max_schedule * conversionFactor;
        slotData[`${discom}_avg_schedule`] =
          item.avg_schedule * conversionFactor;
        slotData[`${discom}_mode_schedule_window_formula`] =
          item.mode_schedule_window_formula * conversionFactor;

        slotData[`${discom}_date_of_min_drawal`] = item.date_of_min_drawal;
        slotData[`${discom}_date_of_max_drawal`] = item.date_of_max_drawal;
        slotData[`${discom}_date_of_min_schedule`] = item.date_of_min_schedule;
        slotData[`${discom}_date_of_max_schedule`] = item.date_of_max_schedule;
      });

      const processedData = Array.from(slotMap.values());

      processedData.sort((a, b) => a.slot - b.slot);

      let finalData = processedData;
      if (selectedBlock.length > 0) {
        finalData = finalData.filter((item) =>
          selectedBlock.map((b) => b.value).includes(item.slot),
        );
      }
      setChartData(finalData);
      setTableData(finalData);

      const uniqueDiscoms = Array.from(
        new Set(filteredDiscomData.map((item) => item.discom)),
      ).sort();
      setChartKeys(uniqueDiscoms);
    }
  }, [
    summaryData,
    discomData,
    totalDiscomData,
    forecastData,
    selectedUnit,
    selectedGroupKeys,
    selectedDiscom,
    selectedForecastType,
    selectedBlock,
  ]);

  const legends = useMemo(() => {
    if (selectedGroupKeys === "RVPN Drawal") {
      const legends: any[] = [];

      if (selectedForecastType.includes("Actual") && isActualDates) {
        legends.push(
          {
            dataKey: "min_drawal",
            name: "Min Drawal (Actual)",
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal" : undefined,
            dateKey: "date_of_min_drawal",
          },
          {
            dataKey: "max_drawal",
            name: "Max Drawal (Actual)",
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal" : undefined,
            dateKey: "date_of_max_drawal",
          },
          {
            dataKey: "avg_drawal",
            name: "Average Drawal (Actual)",
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal" : undefined,
          },
          {
            dataKey: "mode_drawal_window_formula",
            name: "Mode Drawal (Actual)",
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal" : undefined,
          },
        );
      }

      if (selectedForecastType.includes("Forecast D+1") && isForecastDates) {
        legends.push(
          {
            dataKey: "min_drawal_t1",
            name: "Min Drawal (Forecast D+1)",
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal_t1" : undefined,
            dateKey: "date_of_min_drawal_t1",
          },
          {
            dataKey: "max_drawal_t1",
            name: "Max Drawal (Forecast D+1)",
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal_t1" : undefined,
            dateKey: "date_of_max_drawal_t1",
          },
          {
            dataKey: "avg_drawal_t1",
            name: "Average Drawal (Forecast D+1)",
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal_t1" : undefined,
          },
          {
            dataKey: "mode_drawal_window_formula_t1",
            name: "Mode Drawal (Forecast D+1)",
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal_t1" : undefined,
          },
        );
      }

      if (selectedForecastType.includes("Forecast D+2") && isForecastDates) {
        legends.push(
          {
            dataKey: "min_drawal_t2",
            name: "Min Drawal (Forecast D+2)",
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal_t2" : undefined,
            dateKey: "date_of_min_drawal_t2",
          },
          {
            dataKey: "max_drawal_t2",
            name: "Max Drawal (Forecast D+2)",
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal_t2" : undefined,
            dateKey: "date_of_max_drawal_t2",
          },
          {
            dataKey: "avg_drawal_t2",
            name: "Average Drawal (Forecast D+2)",
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal_t2" : undefined,
          },
          {
            dataKey: "mode_drawal_window_formula_t2",
            name: "Mode Drawal (Forecast D+2)",
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal_t2" : undefined,
          },
        );
      }

      return legends;
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      const legends: any[] = [];
      const metricTypes = [
        { field: "min", displayName: "Min" },
        { field: "max", displayName: "Max" },
        { field: "avg", displayName: "Average" },
        { field: "mode", displayName: "Mode" },
      ];

      chartKeys.forEach((key) => {
        metricTypes.forEach((metric) => {
          const fieldSuffix =
            metric.field === "mode"
              ? `mode_drawal_window_formula`
              : `${metric.field}_drawal`;
          const dataKey = `${key}_${fieldSuffix}`;
          const legendName = `${key === "total_discom" ? "Total Discom" : "RVPN"
            } ${metric.displayName} Drawal`;

          const dateKey =
            metric.field === "min" || metric.field === "max"
              ? `${key}_date_of_${metric.field}_drawal`
              : undefined;

          legends.push({
            dataKey: dataKey,
            name: legendName,
            legendColor: undefined,
            stroke: undefined,
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal" : undefined,
            dateKey: dateKey,
          });
        });
      });

      return legends;
    } else if (selectedGroupKeys === "Total Discom Load") {
      const legends: any[] = [];
      const metricTypes = [
        { field: "min", displayName: "Min", hasDate: true },
        { field: "max", displayName: "Max", hasDate: true },
        { field: "avg", displayName: "Average", hasDate: false },
        { field: "mode", displayName: "Mode", hasDate: false },
      ];

      selectedSuffix.forEach(({ suffix, displayName, stackSuffix }) => {
        metricTypes.forEach((metric) => {
          const fieldSuffix =
            metric.field === "mode"
              ? `mode_${suffix}_window_formula`
              : `${metric.field}_${suffix}`;
          const dataKey = fieldSuffix;
          const legendName = `${metric.displayName} ${displayName}`;

          const dateKey = metric.hasDate
            ? `date_of_${metric.field}_${suffix}`
            : undefined;

          legends.push({
            dataKey: dataKey,
            name: legendName,
            legendColor: undefined,
            stroke: undefined,
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? stackSuffix : undefined,
            dateKey: dateKey,
          });
        });
      });

      return legends;
    } else {
      const legends: any[] = [];
      const metricTypes = [
        { field: "min", displayName: "Min", hasDate: true },
        { field: "max", displayName: "Max", hasDate: true },
        { field: "avg", displayName: "Average", hasDate: false },
        { field: "mode", displayName: "Mode", hasDate: false },
      ];

      chartKeys.forEach((discom, discomIndex) => {
        selectedSuffix.forEach(({ suffix, displayName, stackSuffix }) => {
          metricTypes.forEach((metric) => {
            const fieldSuffix =
              metric.field === "mode"
                ? `mode_${suffix}_window_formula`
                : `${metric.field}_${suffix}`;
            const dataKey = `${discom}_${fieldSuffix}`;
            const legendName = `${discom} ${metric.displayName} ${displayName}`;

            const dateKey = metric.hasDate
              ? `${discom}_date_of_${metric.field}_${suffix}`
              : undefined;

            legends.push({
              dataKey: dataKey,
              name: legendName,
              legendColor: undefined,
              stroke: undefined,
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
              dateKey: dateKey,
            });
          });
        });
      });

      return legends;
    }
  }, [
    selectedGroupKeys,
    selectedChartType,
    selectedForecastType,
    chartKeys,
    selectedSuffix,
  ]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const customTooltipFormatter = useCallback(
    (params: any) => {
      const param = Array.isArray(params) ? params[0] : params;
      let items = Array.isArray(params) ? params : [params];

      let title = param.name;
      const isTimeSlot = true;
      if (isTimeSlot) {
        title = getTimeRange(parseInt(param.name)) + ` (${param.name})`;
      }

      const dataPoint = chartData[param.dataIndex];

      const sortedItems = [...items].sort((a, b) => {
        const nameA = (a.seriesName || "").toLowerCase();
        const nameB = (b.seriesName || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });

      const tooltipItems = sortedItems.map((item) => {
        const value = parseFloat(item.value)
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        const bgColor = item.color;

        let displaySeriesName = item.seriesName;

        if (selectedGroupKeys === "RVPN Drawal") {
          if (
            item.seriesName === "Min Drawal (Actual)" &&
            dataPoint?.date_of_min_drawal
          ) {
            displaySeriesName = `Min Drawal (Actual) (${dataPoint.date_of_min_drawal})`;
          } else if (
            item.seriesName === "Max Drawal (Actual)" &&
            dataPoint?.date_of_max_drawal
          ) {
            displaySeriesName = `Max Drawal (Actual) (${dataPoint.date_of_max_drawal})`;
          } else if (
            item.seriesName === "Min Drawal (Forecast D+1)" &&
            dataPoint?.date_of_min_drawal_t1
          ) {
            displaySeriesName = `Min Drawal (Forecast D+1) (${dataPoint.date_of_min_drawal_t1})`;
          } else if (
            item.seriesName === "Max Drawal (Forecast D+1)" &&
            dataPoint?.date_of_max_drawal_t1
          ) {
            displaySeriesName = `Max Drawal (Forecast D+1) (${dataPoint.date_of_max_drawal_t1})`;
          } else if (
            item.seriesName === "Min Drawal (Forecast D+2)" &&
            dataPoint?.date_of_min_drawal_t2
          ) {
            displaySeriesName = `Min Drawal (Forecast D+2) (${dataPoint.date_of_min_drawal_t2})`;
          } else if (
            item.seriesName === "Max Drawal (Forecast D+2)" &&
            dataPoint?.date_of_max_drawal_t2
          ) {
            displaySeriesName = `Max Drawal (Forecast D+2) (${dataPoint.date_of_max_drawal_t2})`;
          }
        } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
          if (item.seriesName.includes("Total Discom")) {
            if (
              item.seriesName.includes("Min Drawal") &&
              dataPoint?.total_discom_date_of_min_drawal
            ) {
              displaySeriesName = `${item.seriesName} (${dataPoint.total_discom_date_of_min_drawal})`;
            } else if (
              item.seriesName.includes("Max Drawal") &&
              dataPoint?.total_discom_date_of_max_drawal
            ) {
              displaySeriesName = `${item.seriesName} (${dataPoint.total_discom_date_of_max_drawal})`;
            }
          } else if (item.seriesName.includes("RVPN")) {
            if (
              item.seriesName.includes("Min Drawal") &&
              dataPoint?.rvpn_date_of_min_drawal
            ) {
              displaySeriesName = `${item.seriesName} (${dataPoint.rvpn_date_of_min_drawal})`;
            } else if (
              item.seriesName.includes("Max Drawal") &&
              dataPoint?.rvpn_date_of_max_drawal
            ) {
              displaySeriesName = `${item.seriesName} (${dataPoint.rvpn_date_of_max_drawal})`;
            }
          }
        } else if (selectedGroupKeys === "Total Discom Load") {
          if (
            item.seriesName === "Min Schedule" &&
            dataPoint?.date_of_min_schedule
          ) {
            displaySeriesName = `Min Schedule (${dataPoint.date_of_min_schedule})`;
          } else if (
            item.seriesName === "Max Schedule" &&
            dataPoint?.date_of_max_schedule
          ) {
            displaySeriesName = `Max Schedule (${dataPoint.date_of_max_schedule})`;
          } else if (
            item.seriesName === "Min Drawal" &&
            dataPoint?.date_of_min_drawal
          ) {
            displaySeriesName = `Min Drawal (${dataPoint.date_of_min_drawal})`;
          } else if (
            item.seriesName === "Max Drawal" &&
            dataPoint?.date_of_max_drawal
          ) {
            displaySeriesName = `Max Drawal (${dataPoint.date_of_max_drawal})`;
          }
        } else if (selectedGroupKeys === "Discom Load") {
          const seriesNameParts = item.seriesName.split(" ");
          const discom = seriesNameParts[0];

          if (item.seriesName.includes("Min Schedule")) {
            const dateField = `${discom}_date_of_min_schedule`;
            if (dataPoint?.[dateField]) {
              displaySeriesName = `${item.seriesName} (${dataPoint[dateField]})`;
            }
          } else if (item.seriesName.includes("Max Schedule")) {
            const dateField = `${discom}_date_of_max_schedule`;
            if (dataPoint?.[dateField]) {
              displaySeriesName = `${item.seriesName} (${dataPoint[dateField]})`;
            }
          } else if (item.seriesName.includes("Min Drawal")) {
            const dateField = `${discom}_date_of_min_drawal`;
            if (dataPoint?.[dateField]) {
              displaySeriesName = `${item.seriesName} (${dataPoint[dateField]})`;
            }
          } else if (item.seriesName.includes("Max Drawal")) {
            const dateField = `${discom}_date_of_max_drawal`;
            if (dataPoint?.[dateField]) {
              displaySeriesName = `${item.seriesName} (${dataPoint[dateField]})`;
            }
          }
        }

        return `
        <div class="tooltip-item" data-name="${item.seriesName?.toLowerCase()}">
          <div class="color-indicator" style="background-color: ${bgColor}"></div>
          <div class="series-name">${displaySeriesName}</div>
          <div class="value">${value} ${selectedUnit}</div>
        </div>
      `;
      });

      const tooltipId = `tooltip-${Date.now()}`;

      return `
      <div class="enhanced-tooltip" id="${tooltipId}">
        <div class="tooltip-header">
          <div class="tooltip-title">${title}</div>
        </div>
        
        <div class="tooltip-content">
          ${tooltipItems.join("")}
        </div>
      </div>
      
      <style>
        .enhanced-tooltip {
          font-family: 'Inter', system-ui, sans-serif;
          width: 100%;
          min-width: 200px;
        }
        .tooltip-header {
          display: flex;
          flex-direction: column;
          gap: 8px;
          justify-content: start;
          align-items: start;
          border-bottom: 1px solid #eaeaea;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .tooltip-title {
          font-weight: 600;
          font-size: 13px;
          color: #333;
        }
        .tooltip-content {
          max-height: 300px;
          overflow-y: auto;
          padding-right: 10px;
        }
        .tooltip-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        .tooltip-item:last-child {
          border-bottom: none;
        }
        .color-indicator {
          width: 10px;
          height: 10px;
          flex-shrink: 0;
          border-radius: 50%;
        }
        .series-name {
          font-weight: 500;
          font-size: 12px;
          color: #333;
          flex: 1;
        }
        .value {
          font-weight: 600;
          font-size: 12px;
          color: #333;
        }
      </style>
    `;
    },
    [chartData, selectedUnit, selectedGroupKeys],
  );

  const getExtraKeys = () => {
    return ["slot_number", "Date"];
  };

  const getExtraLabels = () => {
    return ["Block", "Date"];
  };

  const handleForecastTypeChange = (selected: string[]) => {
    if (selected.length === 0) {
      setSelectedForecastType(["Actual"]);
      return;
    }
    setSelectedForecastType(selected);
    dispatch(setLoadingFilterName(""));
  };

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit,
    block_key: "slot_number",
    extraKeys: getExtraKeys(),
    extraLabels: getExtraLabels(),
    filename: AddDatesWithFileName({
      fileName: "Rajasthan Load Trend Summary",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  function getTypes() {
    if (selectedGroupKeys === "RVPN Drawal") {
      return FORECAST_TYPES;
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      return ["Drawal", "Actual"];
    } else {
      return TYPE;
    }
  }

  function getSelectedTypes() {
    if (selectedGroupKeys === "RVPN Drawal") {
      return selectedForecastType;
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      let types = ["Drawal"];
      if (selectedForecastType.includes("Actual")) {
        types.push("Actual");
      }
      return types;
    } else {
      return selectedSuffix.map((item) => item?.displayName || "");
    }
  }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              onUnitChange(item);
            }}
          />

          {selectedGroupKeys !== "RVPN Drawal" &&
            selectedGroupKeys !== "Total Load & RVPN Drawal" && (
              <FilterMenu
                title="Discom"
                items={DISCOM.map((item) => item.label)}
                selectedItems={selectedDiscom}
                onSelect={(item, index) => {}}
                setSelectedItems={(item) => {
                  if (selectedGroupKeys === "Total Discom Load") {
                    dispatch(setLoadingFilterName(""));
                    return;
                  }
                  onDiscomChange(item);
                }}
              />
            )}

          <FilterMenu
            title="Group By"
            items={GROUP_BY}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => {
              onGroupByChange(index);
            }}
          />

          {selectedGroupKeys === "RVPN Drawal" && (
            <FilterMenu
              title="Type"
              items={FORECAST_TYPES}
              selectedItems={selectedForecastType}
              setSelectedItems={handleForecastTypeChange}
            />
          )}

          {selectedGroupKeys !== "RVPN Drawal" &&
            selectedGroupKeys !== "Total Load & RVPN Drawal" && (
              <FilterMenu
                title="Type"
                items={TYPE}
                selectedItems={selectedSuffix.map(
                  (item) => item?.displayName || "",
                )}
                setSelectedItems={(selected: string[]) => {
                  onTypeChange(selected);
                }}
              />
            )}

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Peak Load (MW)"].value,
                  unit: metrics.load_metrics["Peak Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Peak Forecast Load (MW)"].value,
                  unit: metrics.forecast_metrics["Peak Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Average Load (MW)"].value,
                  unit: metrics.load_metrics["Average Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Average Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Average Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Minimum Load (MW)"].value,
                  unit: metrics.load_metrics["Minimum Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Total Energy (MU)"].value,
                  unit: metrics.load_metrics["Total Energy (MU)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Total Forecast Energy (MU)"]
                      .value,
                  unit: metrics.forecast_metrics["Total Forecast Energy (MU)"]
                    .unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Trend Summary"
              subtitle="Fetching Rajasthan load trend summary data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-80">
                  Load Trend Summary ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV({
                          ...tablePar,
                        });
                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(tablePar);
                      dataTab.label === "Table" &&
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {chartData.length === 0 && !loading && (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#666",
                  }}
                >
                  No data available for the selected Dates.
                </div>
              )}
              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`${selectedGroupKeys}-${selectedChartType.value}-${selectedUnit}`}
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={false}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey="slot"
                  secondXDataKey={undefined}
                  customTooltip={customTooltipFormatter}
                />
              )}

              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={
                <p>
                  DISCOM Energy Reports (RUVITL), Load Shedding Reports (RUVITL)
                </p>
              }
              text="1.	 Actual Drawal = Restricted Demand
           Total Load (Unrestricted Demand) = Actual Drawal + Load Shedding 
2.	 Load Shedding has been distributed across time blocks based on the actual duration of load shedding
3.	MW = Represents the average (MW) when the selected time interval is Date or Month.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
